import {get, HttpResponse} from '@/services/http';
import {log} from '@/utils/logger';
import {Season} from '@/model/core/core-data.model';
import {zeroPad} from '@/utils/common.util';
import dayjs, {Dayjs} from 'dayjs';


const BASE_URL = import.meta.env.VUE_APP_API_ROOT + '/seasons';

const SEASON_SERVICE = {
	all(): Season[] {
		const stop = '0001';
		const seasons: Season[] = [];
		let i = 0;
		let date = dayjs();
		let currentId: string = this.seasonIdForDate(date);
		while (stop !== currentId) {
			i++;
			seasons.push(this.toSeason(currentId));
			date = date.add(-1, 'year');
			currentId = this.seasonIdForDate(date);
			if (i >= 100) {
				log.warn('Too much seasons', i);
				break;
			}
		}
		return seasons;
	},
	current(): Season {
		const seasonId = this.currentId();
		return this.toSeason(seasonId);
	},
	currentId(): string {
		return this.seasonIdForDate(dayjs());
	},
	// a season starts after July 15, as in the backend (SeasonService): 2026-07-16 belongs to "2627"
	seasonIdForDate(date: Dayjs): string {
		const newSeason = date.month() > 6 || (date.month() === 6 && date.date() > 15);
		const startYear = newSeason ? date.year() : date.year() - 1;
		// works only from year 2000 on
		return `${zeroPad(startYear - 2000, 2)}${zeroPad(startYear + 1 - 2000, 2)}`;
	},
	selection(seasonId: string): Promise<HttpResponse<unknown>> {
		return get(`${BASE_URL}/gameCount/${seasonId}`);
	},
	seasonDesc(seasonId: string, long?: boolean, prefix?: string, postfix?: string): string {
		if (!seasonId || seasonId.length !== 4) {
			log.warn(`Invalid season: ${seasonId}`);
			return '';
		}
		let start = Number.parseInt(seasonId.substring(0, 2));
		const end = Number.parseInt(seasonId.substring(2, 4));
		if (long) {
			const century = (start < 70) ? 2000 : 1900;
			start += century;
		}
		const startStr = zeroPad(start, 2);
		const endStr = zeroPad(end, 2);
		prefix = prefix || '';
		postfix = postfix || '';
		return `${prefix}${startStr}/${endStr}${postfix}`;
	},
	seasonsFallback(): string[] {
		return [
			'0102', '0203', '0304', '0405', '0506', '0607', '0708', '0809', '0910', '1011', '1112', '1213', '1314', '1415', '1516', '1617', '1718', '1819', '1920',
		].sort((a, b) => b.localeCompare(a));
	},
	short(seasonId: string) {
		const first = seasonId.slice(0, 2);
		const second = seasonId.slice(2, 4);
		return `${first}/${second}`;
	},
	long(seasonId: string) {
		// works only for year 2000 and after
		return `20${this.short(seasonId)}`;
	},
	display(seasonId: string) {
		return this.long(seasonId);
	},
	toSeason(seasonId: string): Season {
		return {
			seasonId,
			shortDescription: this.short(seasonId),
			longDescription: this.long(seasonId),
			display: this.display(seasonId),
		};
	}
};

export default SEASON_SERVICE;
